import React, { useState } from 'react';
import axios from 'axios';
import { ChevronRight, ChevronLeft, Check, Phone, Mail, MapPin, Clock, MessageSquare, Shield, Zap, Award } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { ScrollReveal } from '../components/ScrollReveal';
import { GoogleMap } from '../components/GoogleMap';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1
    serviceType: '',
    // Step 2
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    // Step 3
    address: '',
    city: '',
    postalCode: '',
    // Step 4
    message: '',
    preferredContact: 'email',
    acceptTerms: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const serviceOptions = [
    { value: 'aides-personne', label: 'Aides à la personne' },
    { value: 'garde-enfant', label: 'Garde d\'enfant' },
    { value: 'menage', label: 'Ménage et repassage' },
    { value: 'bricolage', label: 'Bricolage et jardinage' },
    { value: 'informatique', label: 'Informatique' },
    { value: 'autre', label: 'Autre' }
  ];

  const whyContactUs = [
    {
      icon: <Zap size={32} />,
      title: 'Réponse rapide',
      description: 'Nous vous répondons sous 24h maximum'
    },
    {
      icon: <Shield size={32} />,
      title: 'Devis gratuit',
      description: 'Estimation sans engagement de votre part'
    },
    {
      icon: <Award size={32} />,
      title: 'Conseils personnalisés',
      description: 'Solutions adaptées à vos besoins spécifiques'
    }
  ];

  const contactMethods = [
    {
      icon: <Phone size={28} />,
      title: 'Par téléphone',
      content: '04 81 11 95 91',
      link: 'tel:0481119591',
      description: 'Lun-Ven : 09h00 - 18h00'
    },
    {
      icon: <Phone size={28} />,
      title: 'Par portable',
      content: '07 69 01 69 04',
      link: 'tel:0769016904',
      description: 'Lun-Ven : 09h00 - 18h00'
    },
    {
      icon: <Mail size={28} />,
      title: 'Par email',
      content: 'contact@reseaudaideadomicile.fr',
      link: 'mailto:contact@reseaudaideadomicile.fr',
      description: 'Réponse sous 24h'
    },
    {
      icon: <MapPin size={28} />,
      title: 'En personne',
      content: '23 rue du Dauphiné',
      subcontent: '69003 Lyon',
      description: 'Sur rendez-vous uniquement'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.serviceType !== '';
      case 2:
        return formData.firstName && formData.lastName && formData.email && formData.phone;
      case 3:
        return formData.address && formData.city && formData.postalCode;
      case 4:
        return formData.message && formData.acceptTerms;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    } else {
      toast({
        title: 'Champs requis',
        description: 'Veuillez remplir tous les champs obligatoires',
        variant: 'destructive'
      });
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(4)) {
      toast({
        title: 'Formulaire incomplet',
        description: 'Veuillez accepter les conditions',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/contact`, formData);
      if (response.data.status === 'success') {
        setIsSubmitted(true);
        toast({
          title: 'Demande envoyée !',
          description: 'Nous vous contacterons très bientôt.'
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Une erreur est survenue. Veuillez réessayer.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="page-contact">
        <div className="container">
          <div className="contact-success">
            <div className="success-icon">
              <Check size={64} />
            </div>
            <h1 className="heading-1">Merci pour votre demande !</h1>
            <p className="body-medium">
              Nous avons bien reçu votre message et nous vous contacterons très rapidement.
            </p>
            <button onClick={() => { setIsSubmitted(false); setCurrentStep(1); setFormData({ serviceType: '', firstName: '', lastName: '', email: '', phone: '', address: '', city: '', postalCode: '', message: '', preferredContact: 'email', acceptTerms: false }); }} className="btn-primary">
              Envoyer une nouvelle demande
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-contact">
      <section className="contact-hero">
        <div className="container">
          <h1 className="display-medium">Contactez-nous</h1>
          <p className="body-medium hero-subtitle">
            Une question ? Un projet ? Nous sommes là pour vous accompagner
          </p>
        </div>
      </section>

      {/* Why Contact Us */}
      <section className="why-contact-section">
        <div className="container">
          <div className="network-grid">
            {whyContactUs.map((item, index) => (
              <div key={index} className="network-card why-contact-card">
                <div className="feature-icon">{item.icon}</div>
                <h3 className="heading-2">{item.title}</h3>
                <p className="body-medium">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Methods Cards */}
      <section className="contact-methods-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Plusieurs façons de nous joindre</h2>
            <p className="body-medium">Choisissez le moyen qui vous convient le mieux</p>
          </div>
          <div className="contact-methods-grid">
            {contactMethods.map((method, index) => (
              <div key={index} className="contact-method-card network-card">
                <div className="method-icon">{method.icon}</div>
                <h3 className="heading-2">{method.title}</h3>
                {method.link ? (
                  <a href={method.link} className="method-content">
                    {method.content}
                  </a>
                ) : (
                  <p className="method-content">{method.content}</p>
                )}
                {method.subcontent && (
                  <p className="method-subcontent">{method.subcontent}</p>
                )}
                <p className="method-description">{method.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Contact Form Section */}
      <section id="devis" className="contact-content">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Demandez votre devis gratuit</h2>
            <p className="body-medium">Remplissez le formulaire, nous vous recontactons rapidement</p>
          </div>
          
          <div className="contact-grid">
            {/* Contact Info */}
            <div className="contact-info">
              <div className="info-card network-card">
                <h3 className="heading-2">Informations pratiques</h3>
                
                <div className="info-section">
                  <div className="info-item-header">
                    <Clock size={24} className="info-icon" />
                    <strong>Horaires d'ouverture</strong>
                  </div>
                  <div className="info-details">
                    <p>Lundi – Vendredi : 09h00 – 18h00</p>
                    <p>Samedi : Fermé</p>
                    <p>Dimanche : Fermé</p>
                  </div>
                </div>

                <div className="info-section">
                  <div className="info-item-header">
                    <MapPin size={24} className="info-icon" />
                    <strong>Adresse</strong>
                  </div>
                  <div className="info-details">
                    <p>23 rue du Dauphiné</p>
                    <p>69003 Lyon</p>
                    <a 
                      href="https://www.google.com/maps/search/?api=1&query=23+rue+du+Dauphiné+69003+Lyon" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="map-link"
                    >
                      <MapPin size={16} />
                      Voir sur la carte
                    </a>
                  </div>
                </div>

                <div className="info-section">
                  <div className="info-item-header">
                    <MessageSquare size={24} className="info-icon" />
                    <strong>Besoin d'aide ?</strong>
                  </div>
                  <div className="info-details">
                    <p>Notre équipe est disponible pour répondre à toutes vos questions</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Multi-Step Form */}
            <div className="contact-form-wrapper">
              <div className="form-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${(currentStep / 4) * 100}%` }}
                  ></div>
                </div>
                <div className="progress-steps">
                  {[1, 2, 3, 4].map(step => (
                    <div 
                      key={step} 
                      className={`progress-step ${currentStep >= step ? 'active' : ''}`}
                    >
                      {step}
                    </div>
                  ))}
                </div>
              </div>

              <form onSubmit={handleSubmit} className="contact-form">
                {/* Step 1: Service Selection */}
                {currentStep === 1 && (
                  <div className="form-step">
                    <h3 className="heading-2">Quel service vous intéresse ?</h3>
                    <div className="service-options">
                      {serviceOptions.map(option => (
                        <label key={option.value} className="service-option">
                          <input
                            type="radio"
                            name="serviceType"
                            value={option.value}
                            checked={formData.serviceType === option.value}
                            onChange={handleInputChange}
                          />
                          <span>{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 2: Personal Info */}
                {currentStep === 2 && (
                  <div className="form-step">
                    <h3 className="heading-2">Vos informations</h3>
                    <div className="form-group">
                      <label>Prénom *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Nom *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Téléphone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                )}

                {/* Step 3: Address */}
                {currentStep === 3 && (
                  <div className="form-step">
                    <h3 className="heading-2">Votre adresse</h3>
                    <div className="form-group">
                      <label>Adresse *</label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Ville *</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Code postal *</label>
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                )}

                {/* Step 4: Message & Preferences */}
                {currentStep === 4 && (
                  <div className="form-step">
                    <h3 className="heading-2">Votre message</h3>
                    <div className="form-group">
                      <label>Décrivez vos besoins *</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows="5"
                        required
                      ></textarea>
                    </div>
                    <div className="form-group">
                      <label>Mode de contact préféré</label>
                      <select 
                        name="preferredContact" 
                        value={formData.preferredContact}
                        onChange={handleInputChange}
                      >
                        <option value="email">Email</option>
                        <option value="phone">Téléphone</option>
                      </select>
                    </div>
                    <div className="form-checkbox">
                      <label>
                        <input
                          type="checkbox"
                          name="acceptTerms"
                          checked={formData.acceptTerms}
                          onChange={handleInputChange}
                          required
                        />
                        <span>J'accepte la politique de confidentialité *</span>
                      </label>
                    </div>
                  </div>
                )}

                <div className="form-navigation">
                  {currentStep > 1 && (
                    <button type="button" onClick={prevStep} className="btn-secondary">
                      <ChevronLeft size={20} />
                      Précédent
                    </button>
                  )}
                  {currentStep < 4 ? (
                    <button type="button" onClick={nextStep} className="btn-primary">
                      Suivant
                      <ChevronRight size={20} />
                    </button>
                  ) : (
                    <button type="submit" className="btn-primary" disabled={isSubmitting}>
                      {isSubmitting ? 'Envoi en cours...' : 'Envoyer'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Google Map Section */}
      <section className="map-section">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="section-header">
              <h2 className="heading-1">Notre localisation</h2>
              <p className="body-medium">Basés à Lyon 3ème, nous intervenons dans toute la métropole</p>
            </div>
          </ScrollReveal>
          <ScrollReveal animation="fade-up" delay={200}>
            <GoogleMap address="23 rue du Dauphiné, 69003 Lyon, France" />
          </ScrollReveal>
          <ScrollReveal animation="fade-up" delay={400}>
            <div className="map-zones">
              <h3>Zones d'intervention</h3>
              <div className="zones-list">
                <span className="zone-tag">Lyon 1er - 9ème</span>
                <span className="zone-tag">Villeurbanne</span>
                <span className="zone-tag">Caluire-et-Cuire</span>
                <span className="zone-tag">Vaulx-en-Velin</span>
                <span className="zone-tag">Bron</span>
                <span className="zone-tag">Vénissieux</span>
                <span className="zone-tag">Saint-Priest</span>
                <span className="zone-tag">Oullins</span>
                <span className="zone-tag">Tassin-la-Demi-Lune</span>
                <span className="zone-tag">Écully</span>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};